import React, { useState, useEffect } from "react";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { useNavigate } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import {
  addNewTerritory,
  getDropdowns,
  getPlanningSessions,
  getRegions,
} from "../api/territory";
import Dropdown from "../components/Dropdown";
import Popup from "../../../components/AuthPopup";

const AddNewTerritory = () => {
  const [formData, setFormData] = useState({
    emp_id: "",
    region_entity_key: "",
    identifiers: "",
    session_entity_key: "",
  });
  const [loading, setLoading] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [identifierValues, setIdentifierValues] = useState<any[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const navigate = useNavigate();

  const showErrorModal = (message: string) => {
    setPopupMessage(message || "An unexpected error occurred.");
    setShowPopup(true);
  };

  const showDetailErrors = (detail: any, fallback?: string) => {
    if (Array.isArray(detail) && detail.length > 0) {
      const combined = detail
        .map((err: any) => err?.msg || err?.message || String(err))
        .join("\n");
      showErrorModal(combined);
      return;
    }
    showErrorModal(detail?.msg || detail || fallback || "An unexpected error occurred.");
  };
  const handleChangeDropdown = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "region_entity_key") {
        updated.session_entity_key = "";
        updated.identifiers = "";
      }
      if (name === "session_entity_key") {
        updated.identifiers = "";
      }
      return updated;
    });
  };
  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatOptionLabel = (label?: string, timestamp?: string) => {
    const safeLabel = label || "";
    const formattedTimestamp = formatTimestamp(timestamp);
    return formattedTimestamp ? `${safeLabel} (${formattedTimestamp})` : safeLabel;
  };
  const getLabelClass = (disabled: boolean) =>
    `block font-semibold mb-1 ${disabled ? "text-gray-400" : "text-gray-700"}`;
  const regionOptions = regions.map((region) => ({
    value:
      region.region_entity_key ||
      region.region_identifier ||
      region.id?.toString() ||
      "",
    name: formatOptionLabel(
      region.region_description ||
        region.region_identifier ||
        `Region ${region.region_identifier || region.region_entity_key}`,
      region.created_timestamp
    ),
  }));
  const sessionOptions = sessions.map((session) => ({
    value: session.session_entity_key,
    name: formatOptionLabel(
      session.session_description ||
        session.region_description ||
        `Session ${session.session_entity_key}`,
      session.created_timestamp
    ),
  }));
  const identifierOptions = identifierValues.map((identifier: any) => {
    const value =
      (typeof identifier === "string" && identifier) ||
      identifier?.territory_identifier ||
      identifier?.identifier ||
      identifier?.value ||
      identifier?.id?.toString() ||
      "";
    const label =
      (typeof identifier === "string" && identifier) ||
      identifier?.territory_description ||
      identifier?.territory_identifier ||
      identifier?.name ||
      value;
    return {
      value,
      name: formatOptionLabel(label, identifier?.created_timestamp),
    };
  });
  const isRegionDisabled = regionOptions.length === 0;
  const isSessionDisabled =
    !formData.region_entity_key || sessionOptions.length === 0;
  const isIdentifierDisabled =
    !formData.session_entity_key || identifierOptions.length === 0;
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getInputClass = (field: string) =>
    `w-full border px-3 py-2 rounded ${
      invalidFields.includes(field) ? "border-red-500" : ""
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      emp_id: formData.emp_id,
      territory_identifier: formData.identifiers,
      session_entity_key: formData.session_entity_key
    
    };

    try {
      setLoading(true);
      const emptyFields = Object.entries(formData)
        .filter(([_, value]) => value === null || value === "")
        .map(([key]) => key);

      setInvalidFields(emptyFields);

      if (emptyFields.length > 0) {
        showErrorModal("Please fill all required fields.");
        return;
      }
      const response = await addNewTerritory(payload);
      if (!response) {
        showErrorModal("No data received or malformed response");
        console.warn("No data received or malformed response:", response);
        return;
      }
      toast.success(response?.message);
      setTimeout(() => {
        navigate(URL_MAPPING.TERRITORY);
      }, 2000);
    } catch (error: any) {
      console.error("Failed to fetch territory data:", error);
      if (error?.response?.status === 404) {
        showErrorModal("Route not found, Please try again later");
      } else if (error?.response?.status === 500) {
        showErrorModal(
          error?.response?.data?.detail || "Error while creating the territory"
        );
      } else {
        const detail = error?.response?.data?.detail;
        showDetailErrors(detail);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await getRegions();
        if (!res || !Array.isArray(res)) {
          showErrorModal("Failed to load regions");
          setRegions([]);
          return;
        }
        setRegions(res);
      } catch (error: any) {
        console.error("Failed to fetch regions:", error);
        if (error?.response?.status === 404) {
          showErrorModal("Regions not found, Please try again later");
        } else if (error?.response?.status === 500) {
          showErrorModal("Error while fetching regions");
        } else {
          const detail = error?.response?.data?.detail;
          showDetailErrors(detail, "An unexpected error occurred.");
        }
      }
    };

    fetchRegions();
  }, []);

  useEffect(() => {
    if (!formData.region_entity_key) {
      setSessions([]);
      return;
    }

    const fetchSessions = async () => {
      try {
        const res = await getPlanningSessions(formData.region_entity_key);
        if (!res || !Array.isArray(res)) {
          showErrorModal("Failed to load planning sessions");
          setSessions([]);
          return;
        }
        setSessions(res);
      } catch (error: any) {
        console.error("Failed to fetch planning sessions:", error);
        if (error?.response?.status === 404) {
          showErrorModal("Planning sessions not found, Please try again later");
        } else if (error?.response?.status === 500) {
          showErrorModal("Error while fetching planning sessions");
        } else {
          const detail = error?.response?.data?.detail;
          showDetailErrors(detail, "An unexpected error occurred.");
        }
      }
    };

    fetchSessions();
  }, [formData.region_entity_key]);

  useEffect(() => {
    if (!formData.session_entity_key) {
      setIdentifierValues([]);
      return;
    }

    const fetchIdentifiers = async () => {
      try {
        const res = await getDropdowns(formData.session_entity_key);
        if (!res || !Array.isArray(res)) {
          showErrorModal("Failed to load territory identifiers");
          setIdentifierValues([]);
          return;
        }
        setIdentifierValues(res);
      } catch (error: any) {
        console.error("Failed to fetch territory identifiers:", error);
        if (error?.response?.status === 404) {
          showErrorModal("Territory identifiers not found, Please try again later");
        } else if (error?.response?.status === 500) {
          showErrorModal("Error while fetching territory identifiers");
        } else {
          const detail = error?.response?.data?.detail;
          showDetailErrors(detail, "An unexpected error occurred.");
        }
      }
    };

    fetchIdentifiers();
  }, [formData.session_entity_key]);
  return (
    <LayerAdmin>
      <Popup
        isOpen={showPopup}
        title="Error"
        setShowPopup={setShowPopup}
      >
        <p className="text-sm text-gray-600 whitespace-pre-line">
          {popupMessage}
        </p>
      </Popup>
      <div className="flex items-center px-5 pt-5 gap-3">
        <button
          onClick={() => window.history.back()}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            width="10"
            height="10"
            viewBox="0 0 256 256"
            xmlSpace="preserve"
          >
            <g
              style={{
                stroke: "none",
                strokeWidth: 0,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: "none",
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
            >
              <polygon
                points="20.48,45 59.75,90 69.52,81.48 37.69,45 69.52,8.52 59.75,0 "
                style={{
                  stroke: "none",
                  strokeWidth: 1,
                  strokeDasharray: "none",
                  strokeLinecap: "butt",
                  strokeLinejoin: "miter",
                  strokeMiterlimit: 10,
                  fill: "rgb(0,0,0)",
                  fillRule: "nonzero",
                  opacity: 1,
                }}
                transform="  matrix(1 0 0 1 0 0) "
              />
            </g>
          </svg>
        </button>
        <h3 className="text-xl font-bold text-gray-700">Assign Territory</h3>
      </div>
      <form onSubmit={handleSubmit} className="p-4 lg:w-1/3 sm:w-full">
        <div className="my-2">
          <label className="block font-semibold mb-1">Employee ID *</label>
          <input
            type="text"
            name="emp_id"
            value={formData.emp_id}
            onChange={handleChange}
            className={getInputClass("emp_id")}
            placeholder="Employee Id"
          />
        </div>
        <div className="my-2">
          <label className={getLabelClass(isRegionDisabled)}>Region *</label>
          <Dropdown
            name="region_entity_key"
            value={formData.region_entity_key}
            onChange={handleChangeDropdown}
            options={regionOptions}
            className={getInputClass("region_entity_key")}
            placeholder="Select a region"
            searchable={true}
            disabled={isRegionDisabled}
          />
        </div>
        <div className="my-2">
          <label className={getLabelClass(isSessionDisabled)}>
            Planning Session*
          </label>
          <Dropdown
            name="session_entity_key"
            value={formData.session_entity_key}
            onChange={handleChangeDropdown}
            options={sessionOptions}
            className={getInputClass("session_entity_key")}
            placeholder="Select a session"
            searchable={true}
            disabled={isSessionDisabled}
          />
        </div>

        <div className="my-2">
          <label className={getLabelClass(isIdentifierDisabled)}>
          Territory Identifiers *
          </label>

          <Dropdown
            name="identifiers"
            value={formData.identifiers}
            onChange={handleChangeDropdown}
            options={identifierOptions}
            className={getInputClass("identifiers")}
            placeholder="Select Identifiers "
            searchable={true}
            disabled={isIdentifierDisabled}
          />
          {/* <select
            name="identifiers"
            value={formData.identifiers}
            onChange={handleChange}
            className={getInputClass("identifiers")}
          >
            <option value="">Select Identifiers </option>
            {values.map((values: string) => (
              <option key={values} value={values}>
                {values}
              </option>
            ))}
          </select> */}
        </div>

        
        <button
          type="submit"
          disabled={loading}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
        >
          Assign New Territory {loading && <ButtonLoader />}
        </button>
      </form>
    </LayerAdmin>
  );
};

export default AddNewTerritory;
